import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { Pop } from 'src/app/classes/shared/pop';
import { environment } from 'src/environments/environment';
import { ButtonComponent } from 'ui-library';
import { AuthService } from '../../../services/core/auth.service';

@Component({
  selector: 'app-login',
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatIcon,
    MatInput,
    MatCheckbox,
    MatDividerModule,
    MatProgressSpinnerModule,
    RouterLink,
    TranslateModule
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss'
})

export class LoginComponent implements OnInit, OnDestroy {
  // Variables
  email: string = '';
  password: string = '';
  remember: boolean = false;
  isProcessingCallback: boolean = false;
  private returnUrl: string = '/';


  // MFA
  mfaRequired: boolean = false;
  mfaToken: string = '';
  mfaMethod: string = '';
  mfaCode: string = '';
  mfaTimeLeft: number = 0;
  savingMfa: boolean = false;
  mfaError: string | null = null;
  private mfaTimer: any;

  //Microsoft Azure
  azureLoginUrl: string = `${environment.API_URL}/auth/azure`;
  ssoError: string | null = null;
  destroy$ = new Subject<void>();

  constructor(
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private pop: Pop
  ) { }

  async ngOnInit() {
    if (this.route.snapshot.data['mobileRelay']) {
      const params = new URLSearchParams(window.location.search);
      window.location.href = `com.cad42.unifieldmobile://auth/callback?${params.toString()}`;
      return;
    }

    const code = this.route.snapshot.queryParamMap.get('auth_code');
    const microsoftError = this.route.snapshot.queryParamMap.get('microsoft_error');
    const googleError = this.route.snapshot.queryParamMap.get('google_error');

    if (microsoftError) {
      this.router.navigate(['/auth'], { queryParams: { error: microsoftError, provider: 'microsoft' } });
      return;
    }

    if (googleError) {
      this.router.navigate(['/auth'], { queryParams: { error: googleError, provider: 'google' } });
      return;
    }

    const ssoError = this.route.snapshot.queryParamMap.get('sso_error');

    if (ssoError) {
      this.ssoError = this.translate.instant('users.sso_disabled_error');
      this.router.navigate(['/auth'], { replaceUrl: true });
      return;
    }

    const errorCode = this.route.snapshot.queryParamMap.get('error');

    if (errorCode) {
      const provider = this.route.snapshot.queryParamMap.get('provider');

      if (errorCode === 'method_not_allowed' && provider) {
        this.authMethodBlockedMessage$(provider).pipe(takeUntil(this.destroy$)).subscribe(message => this.pop.error(message));
      } else {
        this.pop.error(this.translateSsoError(errorCode));
      }

      this.router.navigate(['/auth'], { replaceUrl: true });
      return;
    }

    if (code) {
      this.isProcessingCallback = true;
      try {
        await this.authService.completeSsoLogin(code);
        this.router.navigateByUrl('/', { replaceUrl: true });
      } catch (err: any) {
        this.isProcessingCallback = false;
        const errorCode = err?.message ?? 'exchange_failed';

        if (errorCode === 'sso_disabled') {
          this.router.navigate(['/auth'], { replaceUrl: true, queryParams: { sso_error: '1' } });
        } else {
          this.router.navigate(['/auth'], { queryParams: { error: errorCode } });
        }
      }
    } else {
      this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        const accessToken = params['accessToken'];
        const refreshToken = params['refreshToken'];

        if (accessToken && refreshToken) {
          this.authService.saveToken(accessToken);
          this.pop.toast(this.translate.instant('menu.user.login_with_microsoft_success'), 'success', 4000, 'login');
          this.router.navigate(['/']);
        }
      });

      this.resetFields();
    }
  }

  // Main login
  onSubmit(): void {
    const credentials = { email: this.email, password: this.password, remember: this.remember };

    this.authService.login(credentials).pipe(takeUntil(this.destroy$)).subscribe({
      next: (result) => {
        if (result.type === 'mfa_required') {
          this.mfaRequired = true;
          this.mfaToken = result.mfaToken;
          this.mfaMethod = result.mfaMethod;
          this.startMfaTimer(result.expiresAt);
        } else {
          this.authService.saveToken(result.token);
          this.pop.toast(this.translate.instant('menu.user.login_success'), 'success', 4000, 'login');
          this.router.navigateByUrl(this.returnUrl);
          this.resetFields();
        }
      },
      error: (err) => {
        if (err.message === 'method_not_allowed') {
          this.authMethodBlockedMessage$('password').pipe(takeUntil(this.destroy$)).subscribe(message => this.pop.error(message));
        } else {
          this.pop.error(err.message);
        }
      },
    });
  }

  submitMfaCode(): void {
    if (this.mfaCode.length !== 6 || this.mfaTimeLeft === 0 || this.savingMfa) return;
    this.savingMfa = true;
    this.mfaError = null;

    this.authService.verifyMfa(this.mfaToken, this.mfaCode).pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        clearInterval(this.mfaTimer);
        this.pop.toast(this.translate.instant('menu.user.login_success'), 'success', 4000, 'login');
        this.router.navigateByUrl(this.returnUrl);
      },
      error: (err) => {
        this.savingMfa = false;
        this.mfaCode = '';
        this.mfaError = err.message === 'invalid_code'
          ? 'Code incorrect ou expiré.'
          : 'Une erreur est survenue. Réessayez.';
      },
    });
  }

  cancelMfa(): void {
    clearInterval(this.mfaTimer);
    this.mfaRequired = false;
    this.mfaToken = '';
    this.mfaCode = '';
    this.mfaTimeLeft = 0;
    this.mfaError = null;
    this.savingMfa = false;
  }

  private startMfaTimer(expiresAt: string): void {
    clearInterval(this.mfaTimer);

    const update = () => {
      this.mfaTimeLeft = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      if (this.mfaTimeLeft === 0) clearInterval(this.mfaTimer);
    };

    update();
    this.mfaTimer = setInterval(update, 1000);
  }

  // Microsoft Azure login
  redirectToMicrosoft(): void {
    this.authService.loginWithMicrosoft();
  }

  // Google login
  redirectToGoogle(): void {
    this.authService.loginWithGoogle();
  }

  private authMethodBlockedMessage$(method: string) {
    return this.translate.get('users.auth_method_' + method).pipe(
      switchMap(methodLabel => this.translate.get('users.auth_method_blocked', { method: methodLabel }))
    );
  }

  // Translate message SSO error
  private translateSsoError(reason: string): string {
    switch (reason) {
      case 'tenant_consumer_refused':
        return 'Les comptes Microsoft personnels ne sont pas acceptés.';

      case 'email_not_verified':
        return 'Votre adresse email n\'est pas vérifiée.';

      case 'provisioning_refused':
        return 'Votre organisation n\'est pas autorisée. Demandez une invitation à un administrateur.';

      case 'linking_blocked':
        return 'Impossible de lier ce compte à votre compte local.';

      case 'state_invalid':
      case 'code_exchange_failed':
      case 'exchange_failed':
        return 'La session de connexion a expiré. Réessayez.';
        
      default:
        return 'Connexion refusée.';
    }
  }

  // Reset fields
  resetFields() {
    this.email = '';
    this.password = '';
    this.remember = false;
  }

  ngOnDestroy() {
    clearInterval(this.mfaTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
